import React, { useState, useEffect } from "react";
import { analyticsAPI, listAPI, categoryAPI, itemAPI } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

// Online Users Widget Component - only fetches when visible
const OnlineUsersWidget = () => {
  const [onlineCount, setOnlineCount] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    // Only fetch if tab is visible
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  useEffect(() => {
    const fetchOnlineUsers = async () => {
      if (!isVisible) return; // Don't fetch if tab is hidden

      try {
        const response = await analyticsAPI.getOnlineUsersCount();
        setOnlineCount(response.data);
      } catch (error) {
        console.error("Failed to load online users:", error);
      }
    };

    // Initial fetch
    fetchOnlineUsers();

    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchOnlineUsers, 30000);

    // Cleanup
    return () => clearInterval(interval);
  }, [isVisible]); // Re-run when visibility changes

  return (
    <div className="bg-dark-card border border-green-600 rounded-lg p-4 sm:p-6 mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-gray-400 text-xs sm:text-sm mb-2 flex items-center gap-2">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
            </span>
            Users Online Now
          </h3>
          <p className="text-3xl sm:text-4xl font-bold text-green-400">
            {onlineCount}
          </p>
          <p className="text-xs text-gray-500 mt-1">Active in last 5 minutes</p>
        </div>
        <svg
          className="w-12 h-12 sm:w-16 sm:h-16 text-green-500 opacity-20"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
        </svg>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  usePageTitle("Admin Dashboard");
  const [activeTab, setActiveTab] = useState("overview");
  const [analytics, setAnalytics] = useState(null);
  const [lists, setLists] = useState([]);
  const [searchedLists, setSearchedLists] = useState([]);
  const [searchedListsForManagement, setSearchedListsForManagement] = useState(
    [],
  );
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [listItems, setListItems] = useState([]);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [editingList, setEditingList] = useState(null);
  const [showFormattingHelp, setShowFormattingHelp] = useState(false);

  // Item Analytics states
  const [itemAnalyticsListId, setItemAnalyticsListId] = useState(null);
  const [itemAnalytics, setItemAnalytics] = useState([]);
  const [itemAnalyticsSortBy, setItemAnalyticsSortBy] = useState("most");
  const [itemAnalyticsSearchQuery, setItemAnalyticsSearchQuery] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchingLists, setIsSearchingLists] = useState(false);
  const [listSearchQuery, setListSearchQuery] = useState("");
  const [itemSearchQuery, setItemSearchQuery] = useState("");
  const [editingItem, setEditingItem] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [editingCategory, setEditingCategory] = useState(null);
  const [itemPage, setItemPage] = useState(0);

  const [listForm, setListForm] = useState({
    title: "",
    description: "",
    isRanked: false,
    isEnabled: true,
    isPrivate: false,
    categoryIds: [],
    sources: [""],
    adminNotes: "",
  });

  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
  });

  const [itemForm, setItemForm] = useState({
    answer: "",
    alternativeAnswers: [""],
    rankPosition: null,
  });

  useEffect(() => {
    loadAnalytics();
    loadLists();
    loadCategories();
    loadUsers();
  }, []);

  useEffect(() => {
    loadLists();
  }, [currentPage]);

  useEffect(() => {
    // Debounce the search for lists management tab
    const timeoutId = setTimeout(() => {
      searchListsForManagement(searchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  useEffect(() => {
    // Debounce the search for items tab
    const timeoutId = setTimeout(() => {
      searchListsForItems(listSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [listSearchQuery]);

  useEffect(() => {
    // Debounce the search for item analytics tab
    const timeoutId = setTimeout(() => {
      if (itemAnalyticsSearchQuery.trim()) {
        searchListsForItems(itemAnalyticsSearchQuery);
      } else {
        setSearchedLists([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [itemAnalyticsSearchQuery]);

  const loadAnalytics = async () => {
    try {
      const response = await analyticsAPI.getOverallAnalytics();
      setAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    }
  };

  const loadLists = async () => {
    try {
      const response = await listAPI.getAllListsAdmin(currentPage, pageSize);
      setLists(response.data.content);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error("Failed to load lists:", error);
    }
  };

  const loadItemAnalytics = async (listId, sortBy = "most") => {
    if (!listId) return;

    try {
      const response = await analyticsAPI.getItemAnalytics(listId, sortBy);
      setItemAnalytics(response.data);
    } catch (error) {
      console.error("Failed to load item analytics:", error);
      setItemAnalytics([]);
    }
  };

  const searchListsForManagement = async (query) => {
    if (!query.trim()) {
      setSearchedListsForManagement([]);
      setIsSearchingLists(false);
      return;
    }

    setIsSearchingLists(true);
    try {
      // Use the new efficient backend search endpoint
      const response = await listAPI.searchListsAdmin(query, 0, 100);
      setSearchedListsForManagement(response.data.content);
    } catch (error) {
      console.error("Failed to search lists:", error);
      setSearchedListsForManagement([]);
    }
  };

  const searchListsForItems = async (query) => {
    if (!query.trim()) {
      setSearchedLists([]);
      return;
    }

    try {
      // Use the new efficient backend search endpoint
      const response = await listAPI.searchListsAdmin(query, 0, 100);
      setSearchedLists(response.data.content);
    } catch (error) {
      console.error("Failed to search lists:", error);
      setSearchedLists([]);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await categoryAPI.getAllCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to load categories:", error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await analyticsAPI.getAllUsers();
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to load users:", error);
    }
  };

  const loadListItems = async (listId) => {
    try {
      const response = await itemAPI.getItemsByListId(listId);
      setListItems(response.data);
    } catch (error) {
      console.error("Failed to load items:", error);
    }
  };

  const handleCreateList = async (e) => {
    e.preventDefault();
    try {
      // Filter out empty sources before sending
      const cleanedSources = listForm.sources.filter(
        (src) => src.trim() !== "",
      );

      const dataToSend = {
        ...listForm,
        sources: cleanedSources,
      };

      if (editingList) {
        await listAPI.updateList(editingList, dataToSend);
        showMessage("List updated successfully!", "success");
        setEditingList(null);
      } else {
        await listAPI.createList(dataToSend);
        showMessage("List created successfully!", "success");
      }
      await loadLists();
      // Refresh search results if there's an active search in either tab
      if (searchQuery.trim()) {
        await searchListsForManagement(searchQuery);
      }
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
      resetListForm();
    } catch (error) {
      showMessage("Failed to save list", "error");
    }
  };

  const handleEditList = (list) => {
    setEditingList(list.id);
    setListForm({
      title: list.title,
      description: list.description || "",
      isRanked: list.isRanked,
      isEnabled: list.isEnabled,
      isPrivate: list.isPrivate,
      categoryIds: list.categories.map((cat) => cat.id),
      sources: list.sources && list.sources.length > 0 ? list.sources : [""],
      adminNotes: list.adminNotes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEdit = () => {
    setEditingList(null);
    resetListForm();
  };

  const resetListForm = () => {
    setListForm({
      title: "",
      description: "",
      isRanked: false,
      isEnabled: true,
      isPrivate: false,
      categoryIds: [],
      sources: [""],
      adminNotes: "",
    });
  };

  const handleDeleteList = async (listId) => {
    if (
      window.confirm(
        "Are you sure? This will delete the list and all progress!",
      )
    ) {
      try {
        await listAPI.deleteList(listId);
        showMessage("List deleted successfully!", "success");
        await loadLists();
        // Refresh search results if there's an active search in either tab
        if (searchQuery.trim()) {
          await searchListsForManagement(searchQuery);
        }
        if (listSearchQuery.trim()) {
          await searchListsForItems(listSearchQuery);
        }
        setSelectedList(null);
      } catch (error) {
        showMessage("Failed to delete list", "error");
      }
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await categoryAPI.updateCategory(editingCategory, categoryForm);
        showMessage("Category updated successfully!", "success");
        setEditingCategory(null);
      } else {
        await categoryAPI.createCategory(categoryForm);
        showMessage("Category created successfully!", "success");
      }
      await loadCategories();
      setCategoryForm({ name: "", description: "" });
    } catch (error) {
      showMessage("Failed to save category", "error");
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await categoryAPI.deleteCategory(categoryId);
        showMessage("Category deleted successfully!", "success");
        await loadCategories();
      } catch (error) {
        showMessage("Failed to delete category", "error");
      }
    }
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category.id);
    setCategoryForm({
      name: category.name,
      description: category.description || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCancelEditCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ name: "", description: "" });
  };

  // Add alternative answer field
  const handleAddAlternativeAnswer = () => {
    setItemForm({
      ...itemForm,
      alternativeAnswers: [...itemForm.alternativeAnswers, ""],
    });
  };

  // Remove alternative answer field
  const handleRemoveAlternativeAnswer = (index) => {
    const newAlternatives = itemForm.alternativeAnswers.filter(
      (_, i) => i !== index,
    );
    setItemForm({
      ...itemForm,
      alternativeAnswers: newAlternatives.length > 0 ? newAlternatives : [""],
    });
  };

  // Update alternative answer value
  const handleAlternativeAnswerChange = (index, value) => {
    const newAlternatives = [...itemForm.alternativeAnswers];
    newAlternatives[index] = value;
    setItemForm({
      ...itemForm,
      alternativeAnswers: newAlternatives,
    });
  };

  // Add source field
  const handleAddSource = () => {
    setListForm({
      ...listForm,
      sources: [...listForm.sources, ""],
    });
  };

  // Remove source field
  const handleRemoveSource = (index) => {
    const newSources = listForm.sources.filter((_, i) => i !== index);
    setListForm({
      ...listForm,
      sources: newSources.length > 0 ? newSources : [""],
    });
  };

  // Update source value
  const handleSourceChange = (index, value) => {
    const newSources = [...listForm.sources];
    newSources[index] = value;
    setListForm({
      ...listForm,
      sources: newSources,
    });
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (!selectedList) return;

    try {
      // Filter out empty alternative answers before sending
      const cleanedAlternatives = itemForm.alternativeAnswers.filter(
        (alt) => alt.trim() !== "",
      );

      await itemAPI.addItemToList(selectedList, {
        answer: itemForm.answer,
        alternativeAnswers: cleanedAlternatives,
        rankPosition: itemForm.rankPosition,
      });
      // Success message removed - form clears = visual feedback
      await loadListItems(selectedList);
      await loadLists();
      // Refresh search results if there's an active search
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
      setItemForm({ answer: "", alternativeAnswers: [""], rankPosition: null });
    } catch (error) {
      showMessage(
        error.response?.data?.message || "Failed to add item",
        "error",
      );
    }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await itemAPI.deleteItem(itemId);
      await loadListItems(selectedList);
      await loadLists();
      // Refresh search results if there's an active search
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
    } catch (error) {
      showMessage("Failed to delete item", "error");
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item.id);
    setItemForm({
      answer: item.answer,
      alternativeAnswers:
        item.alternativeAnswers && item.alternativeAnswers.length > 0
          ? item.alternativeAnswers
          : [""],
      rankPosition: item.rankPosition || null,
    });
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!editingItem) return;

    try {
      // Filter out empty alternative answers before sending
      const cleanedAlternatives = itemForm.alternativeAnswers.filter(
        (alt) => alt.trim() !== "",
      );

      await itemAPI.updateItem(editingItem, {
        answer: itemForm.answer,
        alternativeAnswers: cleanedAlternatives,
        rankPosition: itemForm.rankPosition,
      });
      await loadListItems(selectedList);
      await loadLists();
      // Refresh search results if there's an active search
      if (listSearchQuery.trim()) {
        await searchListsForItems(listSearchQuery);
      }
      setEditingItem(null);
      setItemForm({ answer: "", alternativeAnswers: [""], rankPosition: null });
    } catch (error) {
      showMessage("Failed to update item", "error");
    }
  };

  const handleCancelEditItem = () => {
    setEditingItem(null);
    setItemForm({ answer: "", alternativeAnswers: [""], rankPosition: null });
  };

  const getFilteredUsers = () => {
    if (!userSearchQuery.trim()) return users;

    const query = userSearchQuery.toLowerCase();
    return users.filter(
      (user) =>
        user.username.toLowerCase().includes(query) ||
        user.email.toLowerCase().includes(query),
    );
  };

  const handleDeleteUser = async (userId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this user? This will delete all their progress!",
      )
    ) {
      try {
        await analyticsAPI.deleteUser(userId);
        showMessage("User deleted successfully!", "success");
        await loadUsers();
        await loadAnalytics();
      } catch (error) {
        showMessage(
          error.response?.data?.message || "Failed to delete user",
          "error",
        );
      }
    }
  };

  const getFilteredItems = () => {
    if (!itemSearchQuery.trim()) return listItems;

    return listItems.filter((item) =>
      item.answer.toLowerCase().includes(itemSearchQuery.toLowerCase()),
    );
  };

  const showMessage = (msg, type) => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
      setMessageType("");
    }, 3000);
  };

  const handleCategoryToggle = (categoryId) => {
    setListForm((prev) => ({
      ...prev,
      categoryIds: prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId],
    }));
  };

  const handleListSelect = async (listId) => {
    setSelectedList(listId);
    if (listId) {
      await loadListItems(listId);
    }
  };

  const handleLeaderboardBan = async (userId) => {
    if (window.confirm("Toggle leaderboard ban for this user?")) {
      try {
        const response = await analyticsAPI.toggleLeaderboardBan(userId);
        if (response.data.success) {
          setMessage(response.data.message);
          setMessageType("success");
          loadUsers(); // Reload user list
        }
      } catch (error) {
        setMessage("Failed to toggle leaderboard ban");
        setMessageType("error");
      }
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1 className="text-2xl sm:text-3xl font-bold text-white mb-6 sm:mb-8">
        Admin Dashboard
      </h1>

      {message && (
        <div
          className={`mb-6 px-4 py-3 rounded text-sm sm:text-base ${
            messageType === "success"
              ? "bg-green-500/10 border border-green-500 text-green-500"
              : "bg-red-500/10 border border-red-500 text-red-500"
          }`}
        >
          {message}
        </div>
      )}

      {/* Mobile Tab Selector */}
      <div className="mb-6 sm:mb-8">
        <div className="sm:hidden">
          <select
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            className="w-full px-4 py-2 bg-dark-card border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="overview">Overview</option>
            <option value="lists">Manage Lists</option>
            <option value="categories">Manage Categories</option>
            <option value="items">Manage Items</option>
            <option value="users">Manage Users</option>
            <option value="item-analytics">Item Analytics</option>
          </select>
        </div>

        {/* Desktop Tabs */}
        <div className="hidden sm:flex space-x-2 md:space-x-4 border-b border-gray-700 overflow-x-auto">
          <button
            onClick={() => setActiveTab("overview")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "overview"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("lists")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "lists"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Lists
          </button>
          <button
            onClick={() => setActiveTab("categories")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "categories"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Categories
          </button>
          <button
            onClick={() => setActiveTab("items")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "items"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Items
          </button>
          <button
            onClick={() => setActiveTab("users")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "users"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Users
          </button>
          <button
            onClick={() => setActiveTab("item-analytics")}
            className={`pb-3 px-3 md:px-4 font-semibold transition whitespace-nowrap text-sm md:text-base ${
              activeTab === "item-analytics"
                ? "text-blue-400 border-b-2 border-blue-400"
                : "text-gray-400 hover:text-white"
            }`}
          >
            Item Analytics
          </button>
        </div>
      </div>

      {activeTab === "overview" && analytics && (
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6">
            Statistics
          </h2>
          <OnlineUsersWidget />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mt-6">
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                Total Users
              </h3>
              <p className="text-3xl sm:text-4xl font-bold text-white">
                {analytics.totalUsers}
              </p>
            </div>
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                Total Lists
              </h3>
              <p className="text-3xl sm:text-4xl font-bold text-blue-400">
                {analytics.totalLists}
              </p>
            </div>
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                Total Categories
              </h3>
              <p className="text-3xl sm:text-4xl font-bold text-green-400">
                {analytics.totalCategories}
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === "lists" && (
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {editingList ? "Edit List" : "Create New List"}
            </h2>
            <form onSubmit={handleCreateList} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                  Title *
                </label>
                <input
                  type="text"
                  value={listForm.title}
                  onChange={(e) =>
                    setListForm({ ...listForm, title: e.target.value })
                  }
                  required
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300 text-sm sm:text-base">
                    Description
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFormattingHelp(!showFormattingHelp)}
                    className="text-blue-400 hover:text-blue-300 text-xs"
                  >
                    {showFormattingHelp ? "Hide" : "Help"}
                  </button>
                </div>

                {showFormattingHelp && (
                  <div className="bg-dark-bg border border-gray-600 rounded p-3 mb-2 text-xs text-gray-300 space-y-1">
                    <p>
                      <code className="bg-gray-700 px-1 rounded">**bold**</code>{" "}
                      = <strong>bold</strong>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">*italic*</code>{" "}
                      = <em>italic</em>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">- item</code> =
                      Bullet point
                    </p>
                  </div>
                )}

                <textarea
                  value={listForm.description}
                  onChange={(e) =>
                    setListForm({ ...listForm, description: e.target.value })
                  }
                  rows="4"
                  placeholder="Supports **bold**, *italic*, lists"
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                  Categories
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto bg-dark-bg p-3 rounded-lg border border-gray-600">
                  {categories.map((cat) => (
                    <label
                      key={cat.id}
                      className="flex items-center space-x-2 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={listForm.categoryIds.includes(cat.id)}
                        onChange={() => handleCategoryToggle(cat.id)}
                        className="rounded"
                      />
                      <span className="text-white text-sm sm:text-base">
                        {cat.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Sources Section */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300 text-sm sm:text-base">
                    Sources (Optional - Admin Only)
                  </label>
                  <button
                    type="button"
                    onClick={handleAddSource}
                    className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Source
                  </button>
                </div>
                <p className="text-xs text-gray-400 mb-2">
                  URLs or references for this list (only visible to admins)
                </p>
                <div className="space-y-2">
                  {listForm.sources.map((source, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={source}
                        onChange={(e) =>
                          handleSourceChange(index, e.target.value)
                        }
                        placeholder={`Source ${index + 1} (e.g., https://example.com)`}
                        className="flex-1 px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                      />
                      {listForm.sources.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSource(index)}
                          className="px-3 bg-red-600/20 hover:bg-red-600/30 border border-red-600 text-red-400 rounded-lg transition text-sm"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Admin Notes Section */}
              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                  Admin Notes (Optional - Private)
                </label>
                <textarea
                  value={listForm.adminNotes}
                  onChange={(e) =>
                    setListForm({ ...listForm, adminNotes: e.target.value })
                  }
                  rows="3"
                  placeholder="Private notes about this list (only you can see this)"
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Use this for reminders, TODO items, or content ideas
                </p>
              </div>

              <div className="space-y-3 bg-dark-bg p-3 sm:p-4 rounded-lg border border-gray-700">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={listForm.isRanked}
                    onChange={(e) =>
                      setListForm({ ...listForm, isRanked: e.target.checked })
                    }
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-white font-medium text-sm sm:text-base">
                      Ranked List
                    </span>
                    <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                      Items have specific order
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={listForm.isEnabled}
                    onChange={(e) =>
                      setListForm({ ...listForm, isEnabled: e.target.checked })
                    }
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-white font-medium text-sm sm:text-base">
                      Enabled
                    </span>
                    <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                      Visible and playable
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={listForm.isPrivate}
                    onChange={(e) =>
                      setListForm({ ...listForm, isPrivate: e.target.checked })
                    }
                    className="w-4 h-4 rounded mt-0.5"
                  />
                  <div className="flex-1">
                    <span className="text-white font-medium text-sm sm:text-base">
                      Private
                    </span>
                    <p className="text-gray-400 text-xs sm:text-sm mt-0.5">
                      Only you can see this
                    </p>
                  </div>
                </label>
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition text-sm sm:text-base"
                >
                  {editingList ? "Update" : "Create"}
                </button>
                {editingList && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-4 sm:px-6 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <div className="mb-4">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                All Lists
              </h2>
              <input
                type="text"
                placeholder="Search lists..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500"
              />
            </div>
            <div className="space-y-3 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
              {(isSearchingLists ? searchedListsForManagement : lists).map(
                (list) => (
                  <div
                    key={list.id}
                    className="bg-dark-bg border border-gray-600 p-3 sm:p-4 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-2 gap-2">
                      <h3 className="text-white font-semibold text-sm sm:text-base line-clamp-2">
                        {list.title}
                      </h3>
                      <div className="flex flex-wrap gap-1 sm:gap-2 flex-shrink-0">
                        {!list.isEnabled && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-red-600/20 text-red-400 text-xs rounded whitespace-nowrap">
                            Disabled
                          </span>
                        )}
                        {list.isPrivate && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-yellow-600/20 text-yellow-400 text-xs rounded whitespace-nowrap">
                            Private
                          </span>
                        )}
                        {list.isRanked && (
                          <span className="px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-600/20 text-blue-400 text-xs rounded whitespace-nowrap">
                            Ranked
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-400 text-xs sm:text-sm mb-3">
                      {list.totalItems} items
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleEditList(list)}
                        className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteList(list.id)}
                        className="text-red-400 hover:text-red-300 text-xs sm:text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ),
              )}
              {isSearchingLists && searchedListsForManagement.length === 0 && (
                <p className="text-gray-400 text-center py-8 text-sm">
                  No lists match your search.
                </p>
              )}
              {!isSearchingLists && lists.length === 0 && (
                <p className="text-gray-400 text-center py-8 text-sm">
                  No lists found.
                </p>
              )}
            </div>

            {!isSearchingLists && totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-4 pt-4 border-t border-gray-700">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentPage === 0}
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                >
                  Prev
                </button>
                <span className="text-gray-400 text-xs sm:text-sm">
                  {currentPage + 1} / {totalPages}
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={currentPage === totalPages - 1}
                  className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "categories" && (
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              {editingCategory ? "Edit Category" : "Create New Category"}
            </h2>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                  Name *
                </label>
                <input
                  type="text"
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  required
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-300 text-sm sm:text-base">
                    Description
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowFormattingHelp(!showFormattingHelp)}
                    className="text-blue-400 hover:text-blue-300 text-xs"
                  >
                    {showFormattingHelp ? "Hide" : "Help"}
                  </button>
                </div>

                {showFormattingHelp && (
                  <div className="bg-dark-bg border border-gray-600 rounded p-3 mb-2 text-xs text-gray-300 space-y-1">
                    <p>
                      <code className="bg-gray-700 px-1 rounded">**bold**</code>{" "}
                      = <strong>bold</strong>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">*italic*</code>{" "}
                      = <em>italic</em>
                    </p>
                    <p>
                      <code className="bg-gray-700 px-1 rounded">- item</code> =
                      Bullet point
                    </p>
                  </div>
                )}

                <textarea
                  value={categoryForm.description}
                  onChange={(e) =>
                    setCategoryForm({
                      ...categoryForm,
                      description: e.target.value,
                    })
                  }
                  rows="4"
                  placeholder="Supports **bold**, *italic*, lists"
                  className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                />
              </div>

              <div className="flex gap-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition text-sm sm:text-base"
                >
                  {editingCategory ? "Update" : "Create"}
                </button>
                {editingCategory && (
                  <button
                    type="button"
                    onClick={handleCancelEditCategory}
                    className="px-4 sm:px-6 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </form>
          </div>

          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              All Categories ({categories.length})
            </h2>
            <div className="space-y-3 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="bg-dark-bg border border-gray-600 p-3 sm:p-4 rounded-lg flex justify-between items-center gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold text-sm sm:text-base truncate">
                      {category.name}
                    </h3>
                    <p className="text-gray-400 text-xs sm:text-sm">
                      {category.listCount}{" "}
                      {category.listCount === 1 ? "list" : "lists"}
                    </p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditCategory(category)}
                      className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="text-red-400 hover:text-red-300 text-xs sm:text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "items" && (
        <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-8">
          <div className="space-y-6">
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                Select a List
              </h2>
              <input
                type="text"
                placeholder="Search lists by name..."
                value={listSearchQuery}
                onChange={(e) => setListSearchQuery(e.target.value)}
                className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500 mb-3"
              />
              <div className="max-h-64 overflow-y-auto bg-dark-bg border border-gray-600 rounded-lg">
                {!listSearchQuery.trim() ? (
                  <p className="text-gray-400 text-center py-8 text-sm">
                    Start typing to search for lists...
                  </p>
                ) : searchedLists.length === 0 ? (
                  <p className="text-gray-400 text-center py-8 text-sm">
                    No lists match your search.
                  </p>
                ) : (
                  searchedLists.map((list) => (
                    <button
                      key={list.id}
                      onClick={() => {
                        handleListSelect(list.id);
                        setItemPage(0);
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-700 transition border-b border-gray-700 last:border-b-0 ${
                        selectedList === list.id ? "bg-blue-600/20" : ""
                      }`}
                    >
                      <div className="flex justify-between items-center">
                        <span className="text-white text-sm font-medium truncate">
                          {list.title}
                        </span>
                        <span className="text-gray-400 text-xs ml-2 flex-shrink-0">
                          {list.totalItems} items
                        </span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>

            {selectedList && (
              <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                  {editingItem ? "Edit Item" : "Add Item"}
                </h2>
                <form
                  onSubmit={editingItem ? handleUpdateItem : handleAddItem}
                  className="space-y-4"
                >
                  <div>
                    <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                      Primary Answer *
                    </label>
                    <input
                      type="text"
                      value={itemForm.answer}
                      onChange={(e) =>
                        setItemForm({ ...itemForm, answer: e.target.value })
                      }
                      required
                      placeholder="e.g., Lionel Messi"
                      className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Main answer shown after completion
                    </p>
                  </div>

                  {/* Alternative Answers Section */}
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-gray-300 text-sm sm:text-base">
                        Alternative Answers (Optional)
                      </label>
                      <button
                        type="button"
                        onClick={handleAddAlternativeAnswer}
                        className="text-blue-400 hover:text-blue-300 text-xs flex items-center gap-1"
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        Add
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">
                      <strong>Casual mode:</strong> Only primary answer is
                      checked.{" "}
                      <strong className="text-green-400">Relaxed mode:</strong>{" "}
                      All answers are checked.
                    </p>
                    <div className="space-y-2">
                      {itemForm.alternativeAnswers.map((alt, index) => (
                        <div key={index} className="flex gap-2">
                          <input
                            type="text"
                            value={alt}
                            onChange={(e) =>
                              handleAlternativeAnswerChange(
                                index,
                                e.target.value,
                              )
                            }
                            placeholder={`Alternative ${index + 1} (e.g., Messi)`}
                            className="flex-1 px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                          />
                          {itemForm.alternativeAnswers.length > 1 && (
                            <button
                              type="button"
                              onClick={() =>
                                handleRemoveAlternativeAnswer(index)
                              }
                              className="px-3 bg-red-600/20 hover:bg-red-600/30 border border-red-600 text-red-400 rounded-lg transition text-sm"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2 text-sm sm:text-base">
                      Rank Position (optional)
                    </label>
                    <input
                      type="number"
                      value={itemForm.rankPosition || ""}
                      onChange={(e) =>
                        setItemForm({
                          ...itemForm,
                          rankPosition: e.target.value
                            ? parseInt(e.target.value)
                            : null,
                        })
                      }
                      className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg transition text-sm sm:text-base"
                    >
                      {editingItem ? "Update" : "Add"}
                    </button>
                    {editingItem && (
                      <button
                        type="button"
                        onClick={handleCancelEditItem}
                        className="px-4 sm:px-6 bg-gray-600 hover:bg-gray-700 text-white py-2 rounded-lg transition text-sm sm:text-base"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </form>
              </div>
            )}
          </div>

          {selectedList &&
            (() => {
              const filteredItems = getFilteredItems();
              const itemsPerPage = 100;
              const totalItemPages = Math.ceil(
                filteredItems.length / itemsPerPage,
              );
              const paginatedItems = filteredItems.slice(
                itemPage * itemsPerPage,
                (itemPage + 1) * itemsPerPage,
              );

              return (
                <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
                  <div className="mb-4">
                    <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
                      Items ({listItems.length})
                    </h2>
                    <input
                      type="text"
                      placeholder="Search items..."
                      value={itemSearchQuery}
                      onChange={(e) => {
                        setItemSearchQuery(e.target.value);
                        setItemPage(0);
                      }}
                      className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div className="space-y-2 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
                    {paginatedItems.length === 0 ? (
                      <p className="text-gray-400 text-center py-8 text-sm sm:text-base">
                        {itemSearchQuery
                          ? "No items match your search."
                          : "No items yet. Add some above!"}
                      </p>
                    ) : (
                      paginatedItems.map((item) => (
                        <div
                          key={item.id}
                          className="bg-dark-bg border border-gray-600 p-3 rounded-lg"
                        >
                          <div className="flex justify-between items-start gap-3">
                            <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                              {item.rankPosition && (
                                <span className="text-gray-400 font-mono text-xs sm:text-sm flex-shrink-0 mt-0.5">
                                  #{item.rankPosition}
                                </span>
                              )}
                              <div className="flex-1 min-w-0">
                                <span className="text-white font-medium text-sm sm:text-base block truncate">
                                  {item.answer}
                                </span>
                                {item.alternativeAnswers &&
                                  item.alternativeAnswers.length > 0 && (
                                    <span className="text-green-400 text-xs mt-1 block">
                                      +{item.alternativeAnswers.length}{" "}
                                      {item.alternativeAnswers.length === 1
                                        ? "alternative"
                                        : "alternatives"}
                                    </span>
                                  )}
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <button
                                onClick={() => handleEditItem(item)}
                                className="text-blue-400 hover:text-blue-300 text-xs sm:text-sm font-medium"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteItem(item.id)}
                                className="text-red-400 hover:text-red-300 text-xs sm:text-sm font-medium"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {totalItemPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-4 pt-4 border-t border-gray-700 flex-wrap">
                      <button
                        onClick={() => setItemPage(Math.max(0, itemPage - 1))}
                        disabled={itemPage === 0}
                        className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                      >
                        Prev
                      </button>

                      <button
                        onClick={() => setItemPage(0)}
                        className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${
                          itemPage === 0
                            ? "bg-blue-600 text-white"
                            : "bg-dark-bg border border-gray-600 text-white hover:bg-gray-700"
                        }`}
                      >
                        1
                      </button>

                      {itemPage > 2 && (
                        <span className="text-gray-400 px-2">...</span>
                      )}

                      {Array.from({ length: totalItemPages }, (_, i) => i)
                        .filter(
                          (i) =>
                            i > 0 &&
                            i < totalItemPages - 1 &&
                            Math.abs(i - itemPage) <= 1,
                        )
                        .map((i) => (
                          <button
                            key={i}
                            onClick={() => setItemPage(i)}
                            className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${
                              itemPage === i
                                ? "bg-blue-600 text-white"
                                : "bg-dark-bg border border-gray-600 text-white hover:bg-gray-700"
                            }`}
                          >
                            {i + 1}
                          </button>
                        ))}

                      {itemPage < totalItemPages - 3 && (
                        <span className="text-gray-400 px-2">...</span>
                      )}

                      {totalItemPages > 1 && (
                        <button
                          onClick={() => setItemPage(totalItemPages - 1)}
                          className={`px-2 sm:px-3 py-1 text-xs sm:text-sm rounded ${
                            itemPage === totalItemPages - 1
                              ? "bg-blue-600 text-white"
                              : "bg-dark-bg border border-gray-600 text-white hover:bg-gray-700"
                          }`}
                        >
                          {totalItemPages}
                        </button>
                      )}

                      <button
                        onClick={() =>
                          setItemPage(
                            Math.min(totalItemPages - 1, itemPage + 1),
                          )
                        }
                        disabled={itemPage === totalItemPages - 1}
                        className="px-2 sm:px-3 py-1 text-xs sm:text-sm bg-dark-bg border border-gray-600 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-700"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
        </div>
      )}

      {activeTab === "users" && (
        <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
          <div className="mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              All Users ({users.length})
            </h2>
            <input
              type="text"
              placeholder="Search users..."
              value={userSearchQuery}
              onChange={(e) => setUserSearchQuery(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500"
            />
          </div>

          {/* Mobile View - Cards */}
          <div className="sm:hidden space-y-3">
            {getFilteredUsers().length === 0 ? (
              <p className="text-gray-400 text-center py-8 text-sm">
                {userSearchQuery
                  ? "No users match your search."
                  : "No users found."}
              </p>
            ) : (
              getFilteredUsers().map((user) => (
                <div
                  key={user.id}
                  className="bg-dark-bg border border-gray-600 p-4 rounded-lg space-y-2"
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-semibold">
                        {user.username}
                      </h3>
                      <p className="text-gray-400 text-sm">{user.email}</p>
                    </div>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        user.isAdmin
                          ? "bg-yellow-600/20 text-yellow-400"
                          : "bg-blue-600/20 text-blue-400"
                      }`}
                    >
                      {user.isAdmin ? "Admin" : "User"}
                    </span>
                  </div>
                  <p className="text-gray-400 text-xs">
                    Joined: {new Date(user.createdAt).toLocaleDateString()}
                  </p>
                  <button
                    onClick={() => handleDeleteUser(user.id)}
                    disabled={user.isAdmin}
                    className="w-full bg-red-600/20 border border-red-600 text-red-400 hover:bg-red-600/30 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition"
                  >
                    {user.isAdmin ? "Cannot Delete Admin" : "Delete User"}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Desktop View - Table */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Username
                  </th>
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Email
                  </th>
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Role
                  </th>
                  <th className="text-left text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Joined
                  </th>
                  <th className="text-right text-gray-400 font-semibold pb-3 px-4 text-sm">
                    Actions
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-300">
                    Leaderboard
                  </th>
                </tr>
              </thead>
              <tbody>
                {getFilteredUsers().length === 0 ? (
                  <tr>
                    <td
                      colSpan="5"
                      className="py-8 text-center text-gray-400 text-sm"
                    >
                      {userSearchQuery
                        ? "No users match your search."
                        : "No users found."}
                    </td>
                  </tr>
                ) : (
                  getFilteredUsers().map((user) => (
                    <tr
                      key={user.id}
                      className="border-b border-gray-700 hover:bg-dark-bg"
                    >
                      <td className="py-4 px-4 text-white text-sm">
                        {user.username}
                      </td>
                      <td className="py-4 px-4 text-gray-400 text-sm">
                        {user.email}
                      </td>
                      <td className="py-4 px-4">
                        <span
                          className={`px-2 py-1 rounded text-xs ${
                            user.isAdmin
                              ? "bg-yellow-600/20 text-yellow-400"
                              : "bg-blue-600/20 text-blue-400"
                          }`}
                        >
                          {user.isAdmin ? "Admin" : "User"}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-gray-400 text-sm">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4 text-right">
                        <button
                          onClick={() => handleDeleteUser(user.id)}
                          disabled={user.isAdmin}
                          className="text-red-400 hover:text-red-300 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            user.isAdmin
                              ? "Cannot delete admin users"
                              : "Delete user"
                          }
                        >
                          Delete
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleLeaderboardBan(user.id)}
                          className={`px-3 py-1 rounded text-sm font-semibold transition ${
                            user.bannedFromLeaderboard
                              ? "bg-green-600 hover:bg-green-700 text-white"
                              : "bg-red-600 hover:bg-red-700 text-white"
                          }`}
                        >
                          {user.bannedFromLeaderboard ? "Unban" : "Ban"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "item-analytics" && (
        <div className="space-y-6">
          {/* List Selector */}
          <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">
              Item Analytics - Select a List
            </h2>
            <input
              type="text"
              placeholder="Search lists by name..."
              value={itemAnalyticsSearchQuery}
              onChange={(e) => setItemAnalyticsSearchQuery(e.target.value)}
              className="w-full px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white placeholder-gray-400 text-sm sm:text-base focus:outline-none focus:border-blue-500 mb-3"
            />
            <div className="max-h-64 overflow-y-auto bg-dark-bg border border-gray-600 rounded-lg">
              {!itemAnalyticsSearchQuery.trim() ? (
                <p className="text-gray-400 text-center py-8 text-sm">
                  Start typing to search for lists...
                </p>
              ) : searchedLists.length === 0 ? (
                <p className="text-gray-400 text-center py-8 text-sm">
                  No lists match your search.
                </p>
              ) : (
                searchedLists.map((list) => (
                  <button
                    key={list.id}
                    onClick={async () => {
                      setItemAnalyticsListId(list.id);
                      await loadItemAnalytics(list.id, itemAnalyticsSortBy);
                    }}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-700 transition border-b border-gray-700 last:border-b-0 ${
                      itemAnalyticsListId === list.id ? "bg-blue-600/20" : ""
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-white text-sm font-medium truncate">
                        {list.title}
                      </span>
                      <span className="text-gray-400 text-xs ml-2 flex-shrink-0">
                        {list.totalItems} items
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Analytics Display */}
          {itemAnalyticsListId && itemAnalytics.length > 0 && (
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <h2 className="text-xl sm:text-2xl font-bold text-white">
                  {itemAnalytics[0]?.gameListTitle} - Item Statistics
                </h2>
                <select
                  value={itemAnalyticsSortBy}
                  onChange={async (e) => {
                    setItemAnalyticsSortBy(e.target.value);
                    await loadItemAnalytics(
                      itemAnalyticsListId,
                      e.target.value,
                    );
                  }}
                  className="px-3 sm:px-4 py-2 bg-dark-bg border border-gray-600 rounded-lg text-white text-sm sm:text-base focus:outline-none focus:border-blue-500"
                >
                  <option value="most">Most Guessed First</option>
                  <option value="least">Least Guessed First</option>
                </select>
              </div>

              {/* Statistics Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-dark-bg border border-gray-600 rounded-lg p-4">
                  <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                    Total Items
                  </h3>
                  <p className="text-2xl sm:text-3xl font-bold text-white">
                    {itemAnalytics.length}
                  </p>
                </div>
                <div className="bg-dark-bg border border-gray-600 rounded-lg p-4">
                  <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                    Most Guessed
                  </h3>
                  <p className="text-2xl sm:text-3xl font-bold text-green-400">
                    {Math.max(...itemAnalytics.map((i) => i.guessCount))}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    {
                      itemAnalytics.find(
                        (i) =>
                          i.guessCount ===
                          Math.max(
                            ...itemAnalytics.map((item) => item.guessCount),
                          ),
                      )?.answer
                    }
                  </p>
                </div>
                <div className="bg-dark-bg border border-gray-600 rounded-lg p-4">
                  <h3 className="text-gray-400 text-xs sm:text-sm mb-2">
                    Least Guessed
                  </h3>
                  <p className="text-2xl sm:text-3xl font-bold text-red-400">
                    {Math.min(...itemAnalytics.map((i) => i.guessCount))}
                  </p>
                  <p className="text-xs text-gray-400 mt-1 truncate">
                    {
                      itemAnalytics.find(
                        (i) =>
                          i.guessCount ===
                          Math.min(
                            ...itemAnalytics.map((item) => item.guessCount),
                          ),
                      )?.answer
                    }
                  </p>
                </div>
              </div>

              {/* Items List */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto">
                {itemAnalytics.map((item, index) => {
                  const maxGuesses = Math.max(
                    ...itemAnalytics.map((i) => i.guessCount),
                  );
                  const percentage =
                    maxGuesses > 0 ? (item.guessCount / maxGuesses) * 100 : 0;

                  return (
                    <div
                      key={item.id}
                      className="bg-dark-bg border border-gray-600 p-3 sm:p-4 rounded-lg"
                    >
                      <div className="flex justify-between items-start gap-3 mb-2">
                        <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                          <span className="text-gray-400 font-mono text-xs sm:text-sm flex-shrink-0">
                            #{index + 1}
                          </span>
                          {item.rankPosition && (
                            <span className="text-blue-400 font-mono text-xs sm:text-sm flex-shrink-0">
                              (Rank #{item.rankPosition})
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <span className="text-white font-medium text-sm sm:text-base block">
                              {item.answer}
                            </span>
                            {item.alternativeAnswers &&
                              item.alternativeAnswers.length > 0 && (
                                <span className="text-green-400 text-xs block mt-1">
                                  +{item.alternativeAnswers.length} alternative
                                  {item.alternativeAnswers.length === 1
                                    ? ""
                                    : "s"}
                                </span>
                              )}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className="text-blue-400 font-bold text-sm sm:text-base block">
                            {item.guessCount}
                          </span>
                          <span className="text-gray-400 text-xs">
                            {item.guessCount === 1 ? "guess" : "guesses"}
                          </span>
                        </div>
                      </div>

                      {/* Visual bar */}
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                        <div
                          className="bg-blue-600 h-full rounded-full transition-all duration-300"
                          style={{ width: `${percentage}%` }}
                        ></div>
                      </div>

                      {/* Warning for very low guess counts */}
                      {item.guessCount < 10 && (
                        <p className="text-yellow-400 text-xs mt-2 flex items-center gap-1">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                          Low guess count - might be too difficult or confusing
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {itemAnalyticsListId && itemAnalytics.length === 0 && (
            <div className="bg-dark-card border border-gray-700 rounded-lg p-4 sm:p-6">
              <p className="text-gray-400 text-center py-8">
                No analytics data available for this list yet. Items will show
                statistics once users start guessing!
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
